/**
 * ============================================================
 * GuideGuru — Calculator Proxy Handler
 * Intercepts calculator form submissions and routes them
 * through the proxy so external API URLs never appear
 * in the browser Network panel.
 *
 * Supports:
 *   1. Personal Year Calculator (predictionyear)
 *   2. Gemstone Calculator
 *
 * Usage:
 *   Add data-guideguru-calculator="predictionyear" or "gemstone"
 *   to the <form> element. Include this script on the page.
 *   The script intercepts form submit, calls the proxy, and
 *   renders the response in a designated results container.
 * ============================================================
 */

(function () {
  'use strict';

  const PROXY_BASE = window.__GUIDEGURU_PROXY_URL || '/apps/guideguru';

  // --- Find all calculator forms ---
  document.addEventListener('DOMContentLoaded', function () {
    // Prediction Year forms
    const predictionForms = document.querySelectorAll('[data-guideguru-calculator="predictionyear"]');
    predictionForms.forEach(function (form) {
      setupPredictionYearForm(form);
    });

    // Gemstone forms
    const gemstoneForms = document.querySelectorAll('[data-guideguru-calculator="gemstone"]');
    gemstoneForms.forEach(function (form) {
      setupGemstoneForm(form);
    });

    // Kundali forms
    const kundaliForms = document.querySelectorAll('[data-guideguru-calculator="kundali"]');
    kundaliForms.forEach(function (form) {
      setupKundaliForm(form);
    });

    // Generic forms — fall back to form action attribute matching
    const allForms = document.querySelectorAll('form');
    allForms.forEach(function (form) {
      const action = form.getAttribute('action') || '';
      if (action.includes('occultmaster.com') || action.includes('predictionyear')) {
        if (!form.hasAttribute('data-guideguru-calculator')) {
          form.setAttribute('data-guideguru-calculator', 'predictionyear');
          setupPredictionYearForm(form);
        }
      }
    });
  });

  /**
   * Setup handler for Personal Year Calculator form.
   */
  function setupPredictionYearForm(form) {
    // Remove direct action to external API
    form.removeAttribute('action');
    form.removeAttribute('target');

    // Find or create global results container
    let section = form.closest('.shopify-section');
    let resultsEl = section ? section.nextElementSibling : null;
    if (!resultsEl || !resultsEl.classList.contains('guideguru-global-results')) {
      resultsEl = document.createElement('div');
      resultsEl.className = 'guideguru-global-results guideguru-results';
      resultsEl.style.cssText = 'margin-top:40px; margin-bottom:40px; display:none; max-width:1200px; margin-left:auto; margin-right:auto; padding:20px; width:100%;';
      if (section) {
        section.parentNode.insertBefore(resultsEl, section.nextSibling);
      } else {
        form.parentElement.appendChild(resultsEl);
      }
    }

    // Remove any associated iframes
    const iframe = form.parentElement.querySelector('iframe[name="prediction_iframe"]')
      || document.querySelector('iframe[name="prediction_iframe"]');
    if (iframe) iframe.remove();

    form.addEventListener('submit', async function (e) {
      e.preventDefault();

      const formData = new FormData(form);
      const params = new URLSearchParams();

      // Map form fields
      const name = formData.get('fullname') || formData.get('name') || '';
      const day = formData.get('day') || '';
      const month = formData.get('month') || '';
      const year = formData.get('year') || '';
      const hour = formData.get('hour') || '';
      const minutes = formData.get('minutes') || '';
      const pyear = formData.get('predictionyear') || formData.get('pyear') || '';

      params.set('name', name);
      params.set('day', day);
      params.set('month', month);
      params.set('year', year);
      if (hour) params.set('hour', hour);
      if (minutes) params.set('minutes', minutes);
      params.set('pyear', pyear);

      // Show loading
      const submitBtn = form.querySelector('[type="submit"]');
      const origBtnText = submitBtn ? submitBtn.value || submitBtn.textContent : 'Submit';
      if (submitBtn) {
        submitBtn.disabled = true;
        if (submitBtn.tagName === 'INPUT') submitBtn.value = 'Loading...';
        else submitBtn.textContent = 'Loading...';
      }

      resultsEl.style.display = 'none';

      try {
        const url = `${PROXY_BASE}/api/calculator/predictionyear?${params.toString()}`;
        const resp = await fetch(url);
        const json = await resp.json();

        if (json.ok && json.html) {
          // Server returned HTML content
          resultsEl.innerHTML = json.html;
          resultsEl.style.display = 'block';
          resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (json.ok && json.data) {
          // Server returned JSON data — render it
          resultsEl.innerHTML = renderPredictionResult(json.data);
          resultsEl.style.display = 'block';
          resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          resultsEl.innerHTML = '<p style="color:#c0392b;">Error: ' + escapeHtml(json.error || 'Unknown error') + '</p>';
          resultsEl.style.display = 'block';
        }
      } catch (err) {
        console.error('Calculator fetch error:', err);
        resultsEl.innerHTML = '<p style="color:#c0392b;">Failed to fetch results. Please try again.</p>';
        resultsEl.style.display = 'block';
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          if (submitBtn.tagName === 'INPUT') submitBtn.value = origBtnText;
          else submitBtn.textContent = origBtnText;
        }
      }
    });
  }

  /**
   * Setup handler for Gemstone Calculator form.
   */
  function setupGemstoneForm(form) {
    form.removeAttribute('action');
    form.removeAttribute('target');

    // Find or create global results container
    let section = form.closest('.shopify-section');
    let resultsEl = section ? section.nextElementSibling : null;
    if (!resultsEl || !resultsEl.classList.contains('guideguru-global-results')) {
      resultsEl = document.createElement('div');
      resultsEl.className = 'guideguru-global-results guideguru-results';
      resultsEl.style.cssText = 'margin-top:40px; margin-bottom:40px; display:none; max-width:1200px; margin-left:auto; margin-right:auto; padding:20px; width:100%;';
      if (section) {
        section.parentNode.insertBefore(resultsEl, section.nextSibling);
      } else {
        form.parentElement.appendChild(resultsEl);
      }
    }

    // Initialize Place Autocomplete
    initPlaceAutocomplete(form);

    form.addEventListener('submit', async function (e) {
      e.preventDefault();

      const formData = new FormData(form);
      const params = new URLSearchParams();

      params.set('full_name', formData.get('full_name') || '');
      params.set('birth_date', formData.get('birth_date') || '');
      params.set('birth_time', formData.get('birth_time') || '');
      params.set('birth_place', formData.get('birth_place') || '');
      params.set('birth_lat', formData.get('birth_lat') || '');
      params.set('birth_long', formData.get('birth_long') || '');
      if (formData.get('user_email')) params.set('user_email', formData.get('user_email'));
      if (formData.get('user_number')) params.set('user_number', formData.get('user_number'));
      if (formData.get('country_code')) params.set('country_code', formData.get('country_code'));
      if (formData.get('user_weight')) params.set('user_weight', formData.get('user_weight'));

      const submitBtn = form.querySelector('[type="submit"]');
      const origBtnText = submitBtn ? submitBtn.value || submitBtn.textContent : 'Submit';
      if (submitBtn) {
        submitBtn.disabled = true;
        if (submitBtn.tagName === 'INPUT') submitBtn.value = 'Loading...';
        else submitBtn.textContent = 'Loading...';
      }

      resultsEl.style.display = 'none';

      try {
        const url = `${PROXY_BASE}/api/calculator/gemstone?${params.toString()}`;
        const resp = await fetch(url);
        const json = await resp.json();

        if (json.ok && json.data && json.data.result) {
          // 1. Tag-based matching: Search Shopify storefront for gemstone
          await Promise.all(json.data.result.map(async (gem) => {
            try {
              const gemBaseName = gem.name.split(' (')[0].toLowerCase().trim();
              
              // Predictive search drops punctuation, so "gem:ruby" becomes "gem ruby",
              // which fails if the product lacks the word "gem". We just search the gem name.
              const searchUrl = `/search/suggest.json?q=${encodeURIComponent(gemBaseName)}&resources[type]=product,collection`;
              const searchResp = await fetch(searchUrl);
              const searchJson = await searchResp.json();
              
              if (searchJson.resources && searchJson.resources.results) {
                const results = searchJson.resources.results;
                
                // Prioritize collection if one exists for the gem
                if (results.collections && results.collections.length > 0) {
                  const matchedCollection = results.collections[0];
                  gem.url = matchedCollection.url;
                  if (matchedCollection.image) gem.image = matchedCollection.image;
                }
                // Fallback to first product
                else if (results.products && results.products.length > 0) {
                  const matchedProduct = results.products[0];
                  gem.url = matchedProduct.url;
                  if (matchedProduct.image) {
                    gem.image = matchedProduct.image;
                  } else if (matchedProduct.featured_image && matchedProduct.featured_image.url) {
                    gem.image = matchedProduct.featured_image.url;
                  }
                }
              }
            } catch (e) {
              console.warn("Could not fetch product for tag:", gem.name, e);
            }
          }));

          resultsEl.innerHTML = renderGemstoneResult(json.data);
          resultsEl.style.display = 'block';
          resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          resultsEl.innerHTML = '<p style="color:#c0392b;">Error: ' + escapeHtml(json.error || 'Unknown error') + '</p>';
          resultsEl.style.display = 'block';
        }
      } catch (err) {
        console.error('Gemstone calculator fetch error:', err);
        resultsEl.innerHTML = '<p style="color:#c0392b;">Failed to fetch results. Please try again.</p>';
        resultsEl.style.display = 'block';
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          if (submitBtn.tagName === 'INPUT') submitBtn.value = origBtnText;
          else submitBtn.textContent = origBtnText;
        }
      }
    });
  }

  /**
   * Initialize Place Autocomplete using Obrio API
   */
  function initPlaceAutocomplete(form) {
    const placeInput = form.querySelector('.gg-place-autocomplete');
    const listEl = form.querySelector('.gg-autocomplete-list');
    const latInput = form.querySelector('input[name="birth_lat"]');
    const lngInput = form.querySelector('input[name="birth_long"]');

    if (!placeInput || !listEl || !latInput || !lngInput) return;

    let timeoutId;

    placeInput.addEventListener('input', function() {
      const query = this.value.trim();
      clearTimeout(timeoutId);

      if (query.length < 3) {
        listEl.style.display = 'none';
        return;
      }

      timeoutId = setTimeout(async function() {
        try {
          const res = await fetch(`https://api-geocode.obrio.net/v1/autocomplete?text=${encodeURIComponent(query)}`);
          const data = await res.json();

          listEl.innerHTML = '';
          if (data && data.features && data.features.length > 0) {
            data.features.forEach(function(feature) {
              const li = document.createElement('li');
              li.style.padding = '10px 15px';
              li.style.cursor = 'pointer';
              li.style.borderBottom = '1px solid #f0f0f0';
              li.style.color = '#333';
              
              // Highlight on hover
              li.addEventListener('mouseenter', () => li.style.background = '#f9f9f9');
              li.addEventListener('mouseleave', () => li.style.background = 'transparent');

              li.textContent = feature.properties.label || feature.properties.name;
              
              li.addEventListener('click', function() {
                placeInput.value = feature.properties.label || feature.properties.name;
                latInput.value = feature.geometry.coordinates[1]; // lat
                lngInput.value = feature.geometry.coordinates[0]; // lon
                listEl.style.display = 'none';
              });
              
              listEl.appendChild(li);
            });
            listEl.style.display = 'block';
          } else {
            listEl.style.display = 'none';
          }
        } catch (err) {
          console.error('Place Autocomplete Error:', err);
          listEl.style.display = 'none';
        }
      }, 400); // 400ms debounce
    });

    // Close list when clicking outside
    document.addEventListener('click', function(e) {
      if (!placeInput.contains(e.target) && !listEl.contains(e.target)) {
        listEl.style.display = 'none';
      }
    });
  }

  /**
   * Render prediction year result as HTML
   */
  function renderPredictionResult(data) {
    // If data is a string (raw response), return as-is
    if (typeof data === 'string') return data;

    // If data is an object, render a structured view
    let html = '<div class="prediction-result" style="background:#f3ebd3; padding:20px; border-radius:10px;">';
    html += '<h3 style="color:#1a1a1a; margin-bottom:15px;">Your Personal Year Prediction</h3>';

    if (data.personal_year) {
      html += '<p><strong>Personal Year Number:</strong> ' + escapeHtml(String(data.personal_year)) + '</p>';
    }
    if (data.prediction) {
      html += '<div style="margin-top:10px;">' + data.prediction + '</div>';
    }

    // Render any other fields dynamically
    Object.keys(data).forEach(function (key) {
      if (key !== 'personal_year' && key !== 'prediction') {
        html += '<p><strong>' + escapeHtml(key.replace(/_/g, ' ')) + ':</strong> ';
        html += typeof data[key] === 'object' ? JSON.stringify(data[key]) : escapeHtml(String(data[key]));
        html += '</p>';
      }
    });

    html += '</div>';
    return html;
  }

  /**
   * Render gemstone result as HTML cards
   */
  function renderGemstoneResult(data) {
    let html = '<div class="gemstone-results" style="margin-top:20px;">';
    
    // Add user details summary block
    html += '<div class="gg-user-summary" style="background:#fff; padding:15px 20px; border-radius:10px; margin-bottom:25px; box-shadow:0 2px 8px rgba(0,0,0,0.05); border-left: 4px solid #e3ac0e;">';
    html += '<h4 style="margin:0 0 10px 0; color:#1a1a1a; font-size:16px;">Details Provided</h4>';
    html += '<div style="display:flex; flex-wrap:wrap; gap:15px; font-size:14px; color:#555;">';
    if (data.name) html += '<div><strong>Name:</strong> ' + escapeHtml(data.name) + '</div>';
    if (data.date) html += '<div><strong>DOB:</strong> ' + escapeHtml(data.date) + '</div>';
    if (data.time) html += '<div><strong>Time:</strong> ' + escapeHtml(data.time) + '</div>';
    if (data.birth_place) html += '<div><strong>Place:</strong> ' + escapeHtml(data.birth_place) + '</div>';
    if (data.user_weight) html += '<div><strong>Weight:</strong> ' + escapeHtml(data.user_weight) + ' kg</div>';
    html += '</div></div>';

    html += '<h3 style="color:#1a1a1a; margin-bottom:20px; font-size:22px;">Your Recommended Gemstones</h3>';

    let gemMap = {};
    try {
      const mapScript = document.getElementById('gemstone-product-map');
      if (mapScript) {
        gemMap = JSON.parse(mapScript.textContent);
      }
    } catch (e) {
      console.warn("Could not parse gemstone map", e);
    }

    if (data.result && Array.isArray(data.result)) {
      html += '<div style="display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:20px;">';

      data.result.forEach(function (gem) {
        // Apply Mapping (partial match)
        let mappedData = null;
        const gemNameLower = (gem.name || '').toLowerCase();
        for (const [mapKey, mapValue] of Object.entries(gemMap)) {
          if (gemNameLower.includes(mapKey)) {
            mappedData = mapValue;
            break;
          }
        }

        if (mappedData) {
          if (mappedData.image) gem.image = mappedData.image;
          if (mappedData.url) gem.url = mappedData.url;
        }

        html += '<div style="background:#f3ebd3; border-radius:12px; overflow:hidden; box-shadow:0 2px 8px rgba(0,0,0,0.1);">';

        if (gem.image) {
          html += '<img src="' + escapeHtml(gem.image) + '" alt="' + escapeHtml(gem.name) + '" style="width:100%; height:200px; object-fit:cover;" loading="lazy" onerror="this.style.display=\'none\'">';
        }

        html += '<div style="padding:20px;">';
        html += '<h4 style="font-size:18px; color:#1a1a1a; margin-bottom:10px;">' + escapeHtml(gem.name) + '</h4>';

        if (gem.significance) {
          html += '<p style="font-size:14px; color:#555; margin-bottom:15px;">' + escapeHtml(gem.significance) + '</p>';
        }

        if (gem.howToWear || gem.wear_metal || gem.wear_finger) {
          html += '<div style="background:#fff; padding:12px; border-radius:8px; font-size:13px; margin-bottom: 15px;">';
          html += '<p style="font-weight:600; margin-bottom:8px;">How to Wear & Recommendations:</p>';
          
          // Support old API format
          if (gem.howToWear) {
            if (gem.howToWear.metal) html += '<p>🔧 <strong>Metal:</strong> ' + escapeHtml(gem.howToWear.metal) + '</p>';
            if (gem.howToWear.finger) html += '<p>👆 <strong>Finger:</strong> ' + escapeHtml(gem.howToWear.finger) + '</p>';
            if (gem.howToWear.timing) html += '<p>🕐 <strong>Timing:</strong> ' + escapeHtml(gem.howToWear.timing) + '</p>';
            if (gem.howToWear.weight) html += '<p>⚖️ <strong>Weight:</strong> ' + escapeHtml(gem.howToWear.weight) + '</p>';
            if (gem.howToWear.mantra) html += '<p>🙏 <strong>Mantra:</strong> <em>' + escapeHtml(gem.howToWear.mantra) + '</em></p>';
          } else {
            // Support new AstrologyAPI format
            if (gem.wear_metal) html += '<p>🔧 <strong>Metal:</strong> ' + escapeHtml(gem.wear_metal) + '</p>';
            if (gem.wear_finger) html += '<p>👆 <strong>Finger:</strong> ' + escapeHtml(gem.wear_finger) + '</p>';
            if (gem.wear_day) html += '<p>📅 <strong>Day:</strong> ' + escapeHtml(gem.wear_day) + '</p>';
            if (gem.weight_caret) html += '<p>⚖️ <strong>Weight:</strong> ' + escapeHtml(gem.weight_caret) + ' Carat</p>';
            if (gem.gem_deity) html += '<p>🙏 <strong>Deity:</strong> ' + escapeHtml(gem.gem_deity) + '</p>';
            if (gem.semi_gem) html += '<p>💎 <strong>Substitute:</strong> ' + escapeHtml(gem.semi_gem) + '</p>';
          }
          
          html += '</div>';
        }

        if (gem.url) {
          html += '<a href="' + escapeHtml(gem.url) + '" style="display:inline-block; margin-top:15px; padding:10px 20px; background:#e3ac0e; color:#1a1a1a; border-radius:6px; text-decoration:none; font-weight:600;">Shop Now</a>';
        }

        html += '</div></div>';
      });

      html += '</div>';
    }

    html += '</div>';
    return html;
  }

  /**
   * Setup handler for Kundali Generation form.
   */
  function setupKundaliForm(form) {
    form.removeAttribute('action');
    form.removeAttribute('target');

    // Find or create global results container
    let section = form.closest('.shopify-section');
    let resultsEl = section ? section.nextElementSibling : null;
    if (!resultsEl || !resultsEl.classList.contains('guideguru-global-results')) {
      resultsEl = document.createElement('div');
      resultsEl.className = 'guideguru-global-results guideguru-results';
      resultsEl.style.cssText = 'margin-top:40px; margin-bottom:40px; display:none; max-width:1200px; margin-left:auto; margin-right:auto; padding:20px; width:100%; text-align:center;';
      if (section) {
        section.parentNode.insertBefore(resultsEl, section.nextSibling);
      } else {
        form.parentElement.appendChild(resultsEl);
      }
    }

    // Initialize Place Autocomplete
    initPlaceAutocomplete(form);

    form.addEventListener('submit', async function (e) {
      e.preventDefault();

      const formData = new FormData(form);
      const params = new URLSearchParams();

      const bDate = formData.get('birth_date') || '';
      const bTime = formData.get('birth_time') || '';
      
      let day = '', month = '', year = '', hour = '', min = '';
      if (bDate) {
        const parts = bDate.split('-');
        year = parts[0];
        month = parts[1];
        day = parts[2];
      }
      if (bTime) {
        const parts = bTime.split(':');
        hour = parts[0];
        min = parts[1];
      }

      params.set('day', day);
      params.set('month', month);
      params.set('year', year);
      params.set('hour', hour);
      params.set('min', min);
      params.set('lat', formData.get('birth_lat') || '');
      params.set('lon', formData.get('birth_long') || '');
      params.set('tzone', '5.5'); // Default India timezone
      params.set('chartType', formData.get('chartType') || 'north');
      params.set('chart_id', 'D1');

      const submitBtn = form.querySelector('[type="submit"]');
      const origBtnText = submitBtn ? submitBtn.value || submitBtn.textContent : 'Generate Kundali';
      if (submitBtn) {
        submitBtn.disabled = true;
        if (submitBtn.tagName === 'INPUT') submitBtn.value = 'Generating...';
        else submitBtn.textContent = 'Generating...';
      }

      resultsEl.style.display = 'none';

      try {
        const url = `${PROXY_BASE}/api/calculator/kundali?${params.toString()}`;
        const resp = await fetch(url);
        const json = await resp.json();

        const userInfo = {
          name: formData.get('full_name') || '',
          date: formData.get('birth_date') || '',
          time: formData.get('birth_time') || '',
          place: formData.get('birth_place') || '',
          lat: formData.get('birth_lat') || '',
          lon: formData.get('birth_long') || ''
        };

        if (json.ok && json.data) {
          resultsEl.innerHTML = renderKundaliResult(json.data, userInfo);
          resultsEl.style.display = 'block';
          resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          resultsEl.innerHTML = '<p style="color:#c0392b;">Error: ' + escapeHtml(json.error || 'Unknown error') + '</p>';
          resultsEl.style.display = 'block';
        }
      } catch (err) {
        console.error('Kundali calculator fetch error:', err);
        resultsEl.innerHTML = '<p style="color:#c0392b;">Failed to generate Kundali. Please try again.</p>';
        resultsEl.style.display = 'block';
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          if (submitBtn.tagName === 'INPUT') submitBtn.value = origBtnText;
          else submitBtn.textContent = origBtnText;
        }
      }
    });
  }

  function renderKundaliResult(data, userInfo) {
    const astro = data.astro_details || {};
    const panchang = data.basic_panchang || {};
    const planets = data.planets || [];
    const vdasha = data.current_vdasha || {};
    
    const processSvg = (svgStr) => {
      if (!svgStr) return '';
      // Ensure the SVG scales properly by adding a viewBox if it doesn't exist, and removing hardcoded width/height
      let processed = svgStr;
      if (!processed.includes('viewBox')) {
        processed = processed.replace('<svg ', '<svg viewBox="0 0 350 350" ');
      }
      return processed;
    };

    const d1 = typeof data.D1 === 'string' ? processSvg(data.D1) : processSvg(data.D1 && data.D1.svg ? data.D1.svg : '');
    const d9 = typeof data.D9 === 'string' ? processSvg(data.D9) : processSvg(data.D9 && data.D9.svg ? data.D9.svg : '');
    const d10 = typeof data.D10 === 'string' ? processSvg(data.D10) : processSvg(data.D10 && data.D10.svg ? data.D10.svg : '');
    const chalit = typeof data.chalit === 'string' ? processSvg(data.chalit) : processSvg(data.chalit && data.chalit.svg ? data.chalit.svg : '');

    let html = `<div class="gg-kundali-dashboard" style="text-align:left; font-family:var(--font-body-family, sans-serif);">`;
    
    // Inject responsive styles for the SVG charts and Mobile Grids
    html += `<style>
      .gg-kundali-dashboard svg {
        width: 100% !important;
        height: auto !important;
        max-width: 100%; 
        display: block;
        margin: 0 auto;
      }
      .gg-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 20px;
        margin-bottom: 30px;
      }
      .gg-grid-dasha {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 10px;
        margin-bottom: 30px;
      }
      @media (max-width: 768px) {
        .gg-grid, .gg-grid-dasha {
          grid-template-columns: 1fr;
        }
      }
    </style>`;

    // Header
    html += `<div style="background:linear-gradient(to right, #1e3a8a, #581c87); padding:15px 20px; border-radius:8px; margin-bottom:20px; box-shadow:0 4px 6px rgba(0,0,0,0.1);">
      <h2 style="color:#fff; margin:0; font-size:24px; display:flex; align-items:center; gap:10px;">
        ✨ Your Astro Details
      </h2>
    </div>`;

    // 3-Column Grid for Basic, Panchang, Astro
    html += `<div class="gg-grid">`;
    
    // Basic Details
    html += renderTableCard("Basic Details", [
      { label: "Name", value: userInfo.name },
      { label: "Date of Birth", value: userInfo.date },
      { label: "Time of Birth", value: userInfo.time },
      { label: "Place", value: userInfo.place },
      { label: "Longitude", value: userInfo.lon },
      { label: "Latitude", value: userInfo.lat }
    ]);

    // Panchang Details
    html += renderTableCard("Panchang Details", [
      { label: "Tithi", value: panchang.tithi },
      { label: "Yog", value: panchang.yog },
      { label: "Nakshatra", value: panchang.nakshatra },
      { label: "Karan", value: panchang.karan },
      { label: "Sunrise", value: panchang.sunrise },
      { label: "Sunset", value: panchang.sunset }
    ]);

    // Astro Details
    html += renderTableCard("Astro Details", [
      { label: "Ascendant", value: astro.ascendant },
      { label: "Varna", value: astro.Varna },
      { label: "Vashya", value: astro.Vashya },
      { label: "Yoni", value: astro.Yoni },
      { label: "Gan", value: astro.Gan },
      { label: "Paya", value: astro.paya },
      { label: "Name Alphabet", value: astro.name_alphabet }
    ]);
    
    html += `</div>`; // End Grid

    // Lagna Chart Section
    html += `<div style="background:linear-gradient(to right, #1e3a8a, #581c87); padding:15px 20px; border-radius:8px; margin-bottom:20px; box-shadow:0 4px 6px rgba(0,0,0,0.1);">
      <h2 style="color:#fff; margin:0; font-size:24px; display:flex; align-items:center; gap:10px;">
        ✨ Lagna or Birth Chart
      </h2>
    </div>`;

    html += `<div class="gg-grid" style="align-items:center;">
      <div style="background:#f9fafb; padding:20px; border-radius:12px; line-height:1.6;">
        <p style="margin-bottom:15px;">Your kundli is ready. You were born on <strong>${escapeHtml(userInfo.date)}</strong> at <strong>${escapeHtml(userInfo.time)}</strong>. Your place of birth is <strong>${escapeHtml(userInfo.place)}</strong>.</p>
        <p style="color:#7f1d1d; font-weight:600;">You are born with the <strong>${escapeHtml(astro.ascendant || '')}</strong> rising. Your birth Nakshatra is <strong>${escapeHtml(panchang.nakshatra || '')}</strong>.</p>
      </div>
      <div style="background:#f3ebd3; padding:15px; border-radius:12px; box-shadow:0 4px 10px rgba(0,0,0,0.05); text-align:center; overflow:hidden;">
        ${d1}
      </div>
    </div>`;

    // Vimshottari Dasha Section
    html += `<div style="background:linear-gradient(to right, #1e3a8a, #581c87); padding:15px 20px; border-radius:8px; margin-bottom:20px; box-shadow:0 4px 6px rgba(0,0,0,0.1);">
      <h2 style="color:#fff; margin:0; font-size:24px; display:flex; align-items:center; gap:10px;">
        ✨ Current Vimshottari Dasha
      </h2>
    </div>`;

    if (vdasha && typeof vdasha === 'object' && !Array.isArray(vdasha)) {
      html += `<div class="gg-grid-dasha">`;
      if (vdasha.major) html += renderDashaCard("Major: " + escapeHtml(vdasha.major.planet), escapeHtml(vdasha.major.start), escapeHtml(vdasha.major.end));
      if (vdasha.minor) html += renderDashaCard("Minor: " + escapeHtml(vdasha.minor.planet), escapeHtml(vdasha.minor.start), escapeHtml(vdasha.minor.end));
      if (vdasha.sub_minor) html += renderDashaCard("Sub-Minor: " + escapeHtml(vdasha.sub_minor.planet), escapeHtml(vdasha.sub_minor.start), escapeHtml(vdasha.sub_minor.end));
      if (vdasha.sub_sub_minor) html += renderDashaCard("Sub-Sub-Minor: " + escapeHtml(vdasha.sub_sub_minor.planet), escapeHtml(vdasha.sub_sub_minor.start), escapeHtml(vdasha.sub_sub_minor.end));
      html += `</div>`;
    } else if (vdasha && vdasha[0] && Array.isArray(vdasha)) {
      html += `<div class="gg-grid-dasha">`;
      vdasha.forEach(dashaBlock => {
        html += renderDashaCard("Dasha: " + escapeHtml(dashaBlock.planet), escapeHtml(dashaBlock.start), escapeHtml(dashaBlock.end));
      });
      html += `</div>`;
    } else {
      html += `<p style="margin-bottom:30px;">Dasha details format unsupported or not found.</p>`;
    }

    // Divsional Charts Section
    html += `<div style="background:linear-gradient(to right, #1e3a8a, #581c87); padding:15px 20px; border-radius:8px; margin-bottom:20px; box-shadow:0 4px 6px rgba(0,0,0,0.1);">
      <h2 style="color:#fff; margin:0; font-size:24px; display:flex; align-items:center; gap:10px;">
        ✨ Kundali Charts
      </h2>
    </div>`;

    html += `<div class="gg-grid" style="text-align:center;">`;
    html += renderChartCard("Chalit Chart", chalit);
    html += renderChartCard("Navamansha Chart", d9);
    html += renderChartCard("Dashamansha Chart", d10);
    html += `</div>`;

    // Planets Table Section
    html += `<div style="background:linear-gradient(to right, #1e3a8a, #581c87); padding:15px 20px; border-radius:8px; margin-bottom:20px; box-shadow:0 4px 6px rgba(0,0,0,0.1);">
      <h2 style="color:#fff; margin:0; font-size:24px; display:flex; align-items:center; gap:10px;">
        ✨ Planetary Positions
      </h2>
    </div>`;

    html += `<div style="overflow-x:auto; margin-bottom:30px; box-shadow:0 2px 5px rgba(0,0,0,0.1); border-radius:8px; border:1px solid #e5e7eb;">
      <table style="width:100%; border-collapse:collapse; min-width:800px; text-align:left; font-size:14px;">
        <thead style="background-color:#f97316; color:#fff;">
          <tr>
            <th style="padding:12px 15px;">Planet</th>
            <th style="padding:12px 15px;">R/D</th>
            <th style="padding:12px 15px;">Sign</th>
            <th style="padding:12px 15px;">Sign Lord</th>
            <th style="padding:12px 15px;">Degree</th>
            <th style="padding:12px 15px;">Nakshatra</th>
            <th style="padding:12px 15px;">Nakshatra Lord</th>
            <th style="padding:12px 15px;">House</th>
          </tr>
        </thead>
        <tbody style="background:#fff;">`;
    
    if (Array.isArray(planets)) {
      planets.forEach(p => {
        html += `<tr style="border-bottom:1px solid #fbd38d;">
          <td style="padding:10px 15px; color:#7f1d1d; font-weight:600;">${escapeHtml(p.name)}</td>
          <td style="padding:10px 15px; color:#7f1d1d; font-weight:600;">${p.isRetro === "true" ? "R" : "D"}</td>
          <td style="padding:10px 15px; color:#7f1d1d; font-weight:600;">${escapeHtml(p.sign)}</td>
          <td style="padding:10px 15px; color:#7f1d1d; font-weight:600;">${escapeHtml(p.signLord)}</td>
          <td style="padding:10px 15px; color:#7f1d1d; font-weight:600;">${parseFloat(p.normDegree || 0).toFixed(2)}</td>
          <td style="padding:10px 15px; color:#7f1d1d; font-weight:600;">${escapeHtml(p.nakshatra)}</td>
          <td style="padding:10px 15px; color:#7f1d1d; font-weight:600;">${escapeHtml(p.nakshatraLord)}</td>
          <td style="padding:10px 15px; color:#7f1d1d; font-weight:600;">${escapeHtml(String(p.house))}</td>
        </tr>`;
      });
    }

    html += `</tbody></table></div>`;
    html += `</div>`; // End Dashboard Wrapper

    return html;
  }

  function renderTableCard(title, rows) {
    let html = `<div style="border-radius:8px; overflow:hidden; border:1px solid #e5e7eb; box-shadow:0 2px 4px rgba(0,0,0,0.05); background:#fff;">
      <table style="width:100%; border-collapse:collapse; font-size:14px; text-align:left;">
        <thead style="background-color:#f97316;">
          <tr><th colspan="2" style="padding:12px; color:#fff; text-align:center; font-size:16px;">${title}</th></tr>
        </thead>
        <tbody style="background:#fff;">`;
    
    rows.forEach(r => {
      html += `<tr style="border-bottom:1px solid #fbd38d;">
        <td style="padding:10px 15px; color:#6b7280; font-weight:500; width:40%;">${escapeHtml(String(r.label))}</td>
        <td style="padding:10px 15px; color:#7f1d1d; font-weight:600;">${escapeHtml(String(r.value || '-'))}</td>
      </tr>`;
    });
    
    html += `</tbody></table></div>`;
    return html;
  }

  function renderDashaCard(title, start, end) {
    return `<div style="border-radius:8px; overflow:hidden; border:1px solid #e5e7eb; box-shadow:0 2px 4px rgba(0,0,0,0.05); background:#fff;">
      <table style="width:100%; border-collapse:collapse; font-size:13px; text-align:left;">
        <thead style="background-color:#f97316;">
          <tr><th style="padding:10px; color:#fff; font-size:14px;">${title}</th></tr>
        </thead>
        <tbody style="background:#fff;">
          <tr style="border-bottom:1px solid #fbd38d;"><td style="padding:8px 10px; color:#7f1d1d; font-weight:600;">Start: ${start}</td></tr>
          <tr><td style="padding:8px 10px; color:#7f1d1d; font-weight:600;">End: ${end}</td></tr>
        </tbody>
      </table>
    </div>`;
  }

  function renderChartCard(title, svg) {
    return `<div style="background:#f3ebd3; border-radius:12px; padding:15px; box-shadow:0 4px 10px rgba(0,0,0,0.05);">
      <h3 style="font-size:18px; font-weight:700; margin-bottom:15px; color:#1a1a1a;">${title}</h3>
      <div style="overflow-x:auto; background:#fff; border-radius:8px; padding:10px;">${svg || '<p>Not available</p>'}</div>
    </div>`;
  }

  function escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
})();
